<template>
  <div class="action-list" :class="`${row2n % 2 ? 'row2n' : 'row2np1'}`">
    <div v-if="actions.length === 0" class="empty-actions">
      {{ t("modules.sqdc.components.eventActionBoard.modals.SQDCActionsField.noActions") }}
    </div>
    <template v-for="(action, index) in renderedActions" :key="action?.id ?? `empty-${index}`">
      <ActionComponent
        v-if="action != null"
        :action="action"
        :event="event"
        :archived="eventArchived"
        :class="getActionClass(index, row2n)"
        :action-types="actionTypes"
        :board-id="boardId"
        :site-id="siteId"
        :github-pull-requests="githubPullRequestsByAction.get(action.id) ?? []"
      />
    </template>
    <div
      v-if="inactiveActions > 0"
      class="inactive-actions lisaui:cursor-pointer"
      @click="showInactive = true"
    >
      {{ t("components.eventAction.action.ActionList.inactiveActions") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, inject, onBeforeUnmount, type PropType, ref, watch } from "vue";
import type { EABActionTypeType, EABEventActionType, EABEventType } from "./EventActionHelper";
import ActionComponent from "./ActionComponent.vue";
import { ActionFilterInjectionKey } from "./filter/FilterTypes";
import { DateTime } from "luxon";
import { SQDCActionStatus, TaskTyp, type TaskOrder } from "@/graphql.ts";
import { compareLISA3Actions } from "../../helper/LISA3EABOrdering";
import type { GithubPullRequestsByAction } from "@/modules/github/GithubPullRequestTypes";

const props = defineProps({
  actions: {
    type: Array as PropType<(EABEventActionType | undefined)[]>,
    required: true,
  },
  event: {
    type: Object as PropType<EABEventType>,
    required: true,
  },
  /** defines the starting background color (striped) of the action list */
  row2n: {
    type: Number,
    required: false,
    default: 0,
  },
  actionTypes: {
    type: Array as PropType<EABActionTypeType[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
  githubPullRequestsByAction: {
    type: Object as PropType<GithubPullRequestsByAction>,
    default: () => new Map(),
  },
  actionOrdering: {
    type: String as PropType<TaskOrder>,
    default: undefined,
  },
});
const emit = defineEmits<{
  renderingChange: [rendering: boolean];
}>();
const showInactive = ref(false);
const actionFilterList = inject(
  ActionFilterInjectionKey,
  ref({ updatedAt: DateTime.now(), actionFilters: [] }),
);

const statusIndex = [SQDCActionStatus.PLAN, SQDCActionStatus.DO, SQDCActionStatus.CHECK];
const visibleActionIds = ref<ReadonlySet<number>>(new Set());
watch(
  [
    () => props.actions.map((action) => action?.id),
    () => actionFilterList.value.updatedAt,
    () => actionFilterList.value.actionFilters,
  ],
  ([_actionIds, filterUpdatedAt, filters], previous) => {
    const filtersChanged =
      previous == null || filterUpdatedAt !== previous[1] || filters !== previous[2];
    const nextIds = new Set(filtersChanged ? [] : visibleActionIds.value);
    for (const action of props.actions) {
      if (action == null || nextIds.has(action.id)) continue;
      const matches = filters.every(
        (filter) =>
          filter.isActionActive?.({
            __typename: "Task",
            assigneeId: action.assigneeId,
            status: statusIndex.indexOf(action.status),
            createdAt: action.createdAt,
            deadline: action.deadline,
            updatedAt: action.updatedAt,
            description: action.SQDCActionTextFields.map((field) => field.value).join(" "),
            id: action.id,
            preventsArchiving: action.status !== SQDCActionStatus.ACT,
            type: TaskTyp.common,
          }) ?? true,
      );
      if (matches) nextIds.add(action.id);
    }
    visibleActionIds.value = nextIds;
  },
  { immediate: true },
);
const activeActions = computed(() => {
  const orderedActions = props.actionOrdering
    ? props.actions.toSorted((left, right) =>
        left == null
          ? right == null
            ? 0
            : 1
          : right == null
            ? -1
            : compareLISA3Actions(left, right, props.actionOrdering!),
      )
    : props.actions;
  if (showInactive.value) {
    return orderedActions;
  }
  return orderedActions.filter((action) => {
    if (action == null) {
      return false;
    }

    return visibleActionIds.value.has(action.id);
  });
});
const renderedActionCount = ref(0);
const renderedActions = computed(() => activeActions.value.slice(0, renderedActionCount.value));
const isRenderingActions = computed(() => renderedActionCount.value < activeActions.value.length);
let renderFrame: number | undefined;
let renderGeneration = 0;

function scheduleRemainingActions() {
  const generation = ++renderGeneration;
  if (renderFrame != null) {
    cancelAnimationFrame(renderFrame);
  }

  const renderNextAction = () => {
    if (generation !== renderGeneration) {
      return;
    }
    renderedActionCount.value = Math.min(renderedActionCount.value + 1, activeActions.value.length);
    if (renderedActionCount.value < activeActions.value.length) {
      renderFrame = requestAnimationFrame(renderNextAction);
    } else {
      renderFrame = undefined;
    }
  };

  if (renderedActionCount.value < activeActions.value.length) {
    renderFrame = requestAnimationFrame(renderNextAction);
  }
}

watch(
  activeActions,
  (nextActions, previousActions) => {
    const previousActionsArePrefix =
      previousActions != null &&
      previousActions.length > 0 &&
      nextActions.length >= previousActions.length &&
      previousActions.every((action, index) => nextActions[index]?.id === action?.id);

    renderedActionCount.value = previousActionsArePrefix
      ? Math.min(renderedActionCount.value, nextActions.length)
      : Math.min(1, nextActions.length);
    scheduleRemainingActions();
  },
  { immediate: true },
);
watch(isRenderingActions, (rendering) => emit("renderingChange", rendering), {
  immediate: true,
});
const inactiveActions = computed(() => {
  return props.actions.length - activeActions.value.length;
});
const eventArchived = computed(() => props.event.deletedAt != null);
function getActionClass(index: number, row2n: number) {
  return {
    row2n: (index + row2n) % 2,
    row2np1: !((index + row2n) % 2),
    borderTop: index !== 0,
  };
}

onBeforeUnmount(() => {
  renderGeneration += 1;
  if (renderFrame != null) {
    cancelAnimationFrame(renderFrame);
  }
  if (isRenderingActions.value) {
    emit("renderingChange", false);
  }
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.action-list {
  border: 1px solid var(--lisa-secondary);
  border-left: none;
  border-radius: 3px;
  .borderTop {
    border-top: 1px solid var(--lisa-secondary);
  }
  .action:first-of-type {
    border-top: 0;
  }
  .inactive-actions {
    padding: 0.5em;
    text-align: center;
  }

  .empty-actions {
    padding: 0.75rem;
    color: var(--p-text-muted-color);
    text-align: center;
  }
}
</style>
